import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams } from "react-router-dom";
import {
  GovItemDetail,
  GovKind,
  getItem,
  governanceErrorMessage,
  governanceErrorStatus,
  useGovernanceSettings,
} from "../../../utils/icGovernance";
import ActionsCard from "./detail/ActionsCard";
import Breadcrumb from "./detail/Breadcrumb";
import CommentsSection from "./detail/CommentsSection";
import HistorySection from "./detail/HistorySection";
import { DetailsCard, EvidenceCard, LinksCard } from "./detail/InfoCards";
import SideCard from "./detail/SideCard";
import SignOffCard from "./detail/SignOffCard";
import SuccessBanner from "./detail/SuccessBanner";
import { GovSettingsOrNull, ItemActionProps, SuccessNotice } from "./detail/types";
import { FAINT, MONO, MUTED, TEXT } from "./detail/ui";
import {
  CARD_BORDER,
  GOVERNANCE_BASE,
  PRIMARY,
  PriorityChip,
  StatusChip,
  kindLabel,
  partyLabel,
  shortDate,
  tabForKind,
} from "./governanceStatus";
import ChangeRequestPanel from "./panels/ChangeRequestPanel";
import IssuePanel from "./panels/IssuePanel";
import ReleasePanel from "./panels/ReleasePanel";
import TaskPanel from "./panels/TaskPanel";
import UatTestPanel from "./panels/UatTestPanel";

const PREFIX_KIND: Record<string, GovKind> = {
  CR: "CHANGE_REQUEST",
  REL: "RELEASE",
  UAT: "UAT_TEST",
  T: "TASK",
  ISS: "ISSUE",
};

/** Best guess of the kind from a display id (TD-CR-4), for the back link before the item loads. */
const kindFromDisplayId = (id: string): GovKind | null => {
  const m = /-([A-Za-z]+)-\d+$/.exec(id);
  return m ? PREFIX_KIND[m[1].toUpperCase()] ?? null : null;
};

const ItemBreadcrumb = ({ kind, displayId }: { kind: GovKind | null; displayId: string }) => {
  const tab = kind ? tabForKind(kind) : null;
  return (
    <Breadcrumb
      to={tab?.path ?? GOVERNANCE_BASE}
      label={tab?.label ?? "Project"}
      trail={["Project", ...(tab ? [tab.label] : []), displayId]}
    />
  );
};

const ItemHeader = ({
  item,
  settings,
  loading,
  onRefresh,
}: {
  item: GovItemDetail;
  settings: GovSettingsOrNull;
  loading: boolean;
  onRefresh: () => void;
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="baseline" useFlexGap sx={{ flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: MONO, fontWeight: 700, color: MUTED, fontSize: 14 }}>{item.displayId}</Typography>
        <Typography
          variant="overline"
          sx={{ color: PRIMARY, fontWeight: 700, lineHeight: 1.5, letterSpacing: "0.08em" }}
        >
          {kindLabel(item.kind)}
        </Typography>
      </Stack>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: TEXT, overflowWrap: "anywhere" }}>
        {item.title}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mt: 1 }}>
        <StatusChip kind={item.kind} status={item.status} />
        <PriorityChip priority={item.priority} />
        <Typography variant="body2" sx={{ color: MUTED }}>
          Opened by <b>{item.createdBy || "—"}</b> on {shortDate(item.createdAt)}
          {item.leadParty ? (
            <>
              {" "}
              · Lead party: <b>{partyLabel(item.leadParty, settings)}</b>
            </>
          ) : null}{" "}
          · Updated {shortDate(item.updatedAt)}
        </Typography>
      </Stack>
    </Box>
    <Tooltip title="Refresh">
      <span>
        <IconButton aria-label="Refresh item" onClick={onRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

const KindPanel = ({ item, settings, onUpdated, onReload }: { item: GovItemDetail; settings: GovSettingsOrNull } & ItemActionProps) => {
  switch (item.kind) {
    case "ISSUE":
      return <IssuePanel item={item} />;
    case "TASK":
      return <TaskPanel item={item} />;
    case "CHANGE_REQUEST":
      return <ChangeRequestPanel item={item} settings={settings} onUpdated={onUpdated} onReload={onReload} />;
    case "RELEASE":
      return <ReleasePanel item={item} />;
    case "UAT_TEST":
      return <UatTestPanel item={item} onUpdated={onUpdated} onReload={onReload} />;
    default:
      return null;
  }
};

// /dashboard/governance/items/:id (:id = display id) — one governance item:
// header, description, the kind's panel, side cards (sign-off, actions,
// details, evidence, links), comments and the history log (expanded).
// Every control is driven by the item's `permissions` / `canDecideSignOff`.
const ItemDetail = () => {
  const { id = "" } = useParams();
  const { settings } = useGovernanceSettings();
  const gov: GovSettingsOrNull = settings.enabled ? settings : null;
  const [item, setItem] = useState<GovItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [notice, setNotice] = useState<SuccessNotice | null>(null);
  const requestIdRef = useRef(0);
  // ObjectId of the item on screen: action answers for another item are dropped.
  const currentIdRef = useRef<string | null>(null);

  const load = useCallback(
    (opts: { quiet?: boolean } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!opts.quiet) setLoading(true);
      return getItem(id)
        .then((detail) => {
          if (requestId !== requestIdRef.current) return;
          currentIdRef.current = detail.id;
          setItem(detail);
          setError(null);
          setNotFound(false);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          console.error("Failed to load governance item:", err);
          if (governanceErrorStatus(err) === 404) {
            currentIdRef.current = null;
            setNotFound(true);
            setItem(null);
          } else {
            setError(governanceErrorMessage(err));
          }
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    currentIdRef.current = null;
    setItem(null);
    setNotice(null);
    setNotFound(false);
    setError(null);
    load();
  }, [load]);

  const reload = useCallback(() => {
    load();
  }, [load]);
  const refreshQuiet = useCallback(() => load({ quiet: true }), [load]);

  const handleUpdated = useCallback((detail: GovItemDetail, next: SuccessNotice) => {
    if (!detail || detail.id !== currentIdRef.current) return;
    requestIdRef.current++; // an older in-flight load must not overwrite this answer
    setItem(detail);
    setError(null);
    setLoading(false);
    setNotice(next);
  }, []);

  if (loading && !item) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ItemBreadcrumb kind={kindFromDisplayId(id)} displayId={id} />
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: PRIMARY, mt: 6 }} />
        </Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ItemBreadcrumb kind={kindFromDisplayId(id)} displayId={id} />
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT }}>
            Item not found
          </Typography>
          <Typography variant="body2" sx={{ color: FAINT, mt: 1 }}>
            {id} does not exist in this project.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ItemBreadcrumb kind={kindFromDisplayId(id)} displayId={id} />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error || "Could not load this item."}
        </Alert>
      </Box>
    );
  }

  const actionProps: ItemActionProps = { onUpdated: handleUpdated, onReload: reload };

  return (
    <Box sx={{ padding: "20px", textAlign: "left", maxWidth: 1280 }}>
      <ItemBreadcrumb kind={item.kind} displayId={item.displayId} />
      <SuccessBanner notice={notice} onClose={() => setNotice(null)} />
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      {/* Phones/tablets: top, side cards, then comments + history. From 900 px
          the side cards form a right-hand column spanning both rows. */}
      <Box
        key={item.id}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 320px", lg: "minmax(0, 1fr) 360px" },
          gridTemplateAreas: { xs: '"top" "side" "bottom"', md: '"top side" "bottom side"' },
          gridTemplateRows: { md: "auto 1fr" },
          columnGap: 3,
          rowGap: 2,
          alignItems: "start",
        }}
      >
        <Stack spacing={2} sx={{ gridArea: "top", minWidth: 0 }}>
          <ItemHeader item={item} settings={gov} loading={loading} onRefresh={reload} />
          <SideCard title="Description">
            <Typography variant="body2" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere", color: TEXT }}>
              {item.description || "No description."}
            </Typography>
          </SideCard>
          <KindPanel item={item} settings={gov} {...actionProps} />
        </Stack>

        <Stack spacing={2} sx={{ gridArea: "side", minWidth: 0 }}>
          {item.signOffEnabled ? <SignOffCard item={item} {...actionProps} /> : null}
          <ActionsCard item={item} {...actionProps} />
          <DetailsCard item={item} settings={gov} />
          <EvidenceCard item={item} />
          <LinksCard item={item} />
        </Stack>

        <Stack spacing={2} sx={{ gridArea: "bottom", minWidth: 0 }}>
          <CommentsSection item={item} onRefresh={refreshQuiet} />
          <HistorySection history={item.history} />
        </Stack>
      </Box>
    </Box>
  );
};

export default ItemDetail;
